/**
 * mcid-reassociate — pure helpers for the Path B editor gestures.
 *
 * The three editor gestures (resize-bigger absorb, resize-smaller kick-out,
 * click-drag-to-create) all bottom out in three pure rect-vs-MCID-
 * centroid math operations:
 *
 *   - findMcidsInBbox(mcids, rect)
 *       Filter the IR's per-MCID centroid list to MCIDs whose centroid
 *       falls inside `rect`. Used by all three gestures.
 *
 *   - tightenBboxToMcids(mcids)
 *       Shrink a drawn rect to the union of the contained MCIDs' glyph
 *       bboxes (snap-to-content). Used by the click-drag-to-create
 *       gesture: the user draws "around the area I want" and the system
 *       auto-tightens to the actual glyph bounds.
 *
 *   - mcidsByOwner(mcids, ownerLookup)
 *       Group MCIDs by their current owning struct elem. Used by the
 *       resize-bigger gesture to detect when a typed-role struct is
 *       being absorbed (→ confirmation modal) vs an unparented MCID
 *       (silent absorb).
 *
 * All math runs in PDF user-space coordinates (origin bottom-left,
 * y grows up). The IR's `mcids` array carries `centroid: { x, y }` and
 * `bbox: { x, y, w, h }` already in PDF user-space.
 *
 * The ResizeReassociateController + DrawCreateController defined later
 * in this file consume these helpers + the existing pointer-event
 * machinery (drag-move.js, resize-move.js) to wire the three gestures
 * to the `reassociate-mcids` server op.
 *
 * Pure helpers run in node tests; the controllers are browser-only.
 *
 * @module mcid-reassociate
 */
// ─── Pure helpers ────────────────────────────────────────────────────────────
export function findMcidsInBbox(mcids, pageNum, rect) {
    if (!Array.isArray(mcids) || mcids.length === 0)
        return [];
    if (!Array.isArray(rect) || rect.length !== 4)
        return [];
    const [rx, ry, rw, rh] = rect;
    if (!Number.isFinite(rx) || !Number.isFinite(ry))
        return [];
    if (!(rw > 0) || !(rh > 0))
        return [];
    const xMax = rx + rw;
    const yMax = ry + rh;
    const out = [];
    for (const m of mcids) {
        if (m?.page !== pageNum)
            continue;
        const c = m.centroid;
        if (!c)
            continue;
        if (c.x >= rx && c.x <= xMax && c.y >= ry && c.y <= yMax) {
            out.push(m);
        }
    }
    return out;
}
export function tightenBboxToMcids(mcids, fallback) {
    if (!Array.isArray(mcids) || mcids.length === 0) {
        return Array.isArray(fallback) ? fallback.slice() : [0, 0, 0, 0];
    }
    let xMin = Infinity, yMin = Infinity, xMax = -Infinity, yMax = -Infinity;
    let any = false;
    for (const m of mcids) {
        if (!m?.bbox)
            continue;
        const b = m.bbox;
        const bx = Number(b.x), by = Number(b.y);
        const bw = Number(b.w), bh = Number(b.h);
        if (!Number.isFinite(bx) || !Number.isFinite(by)
            || !Number.isFinite(bw) || !Number.isFinite(bh))
            continue;
        if (bx < xMin)
            xMin = bx;
        if (by < yMin)
            yMin = by;
        if (bx + bw > xMax)
            xMax = bx + bw;
        if (by + bh > yMax)
            yMax = by + bh;
        any = true;
    }
    if (!any)
        return Array.isArray(fallback) ? fallback.slice() : [0, 0, 0, 0];
    return [xMin, yMin, xMax - xMin, yMax - yMin];
}
export function mcidsByOwner(mcids, ownerLookup) {
    const out = new Map();
    if (!Array.isArray(mcids))
        return out;
    if (typeof ownerLookup !== "function")
        return out;
    for (const m of mcids) {
        if (!m)
            continue;
        let owner = null;
        try {
            owner = ownerLookup(m.page, m.mcid);
        }
        catch {
            owner = null;
        }
        if (!out.has(owner))
            out.set(owner, []);
        out.get(owner).push(m);
    }
    return out;
}
export function detectAbsorbedMcids({ mcidsOnPage, resizingStructId, ownerLookup, oldBbox, newBbox, }) {
    const absorbed = [];
    if (!Array.isArray(mcidsOnPage))
        return { absorbed, byOwner: new Map() };
    if (!Array.isArray(oldBbox) || !Array.isArray(newBbox))
        return { absorbed, byOwner: new Map() };
    const inNew = findMcidsInBbox(mcidsOnPage, mcidsOnPage[0]?.page ?? -1, newBbox);
    const oldXMax = oldBbox[0] + oldBbox[2];
    const oldYMax = oldBbox[1] + oldBbox[3];
    for (const m of inNew) {
        // Must be a NEW occupant — was not inside oldBbox.
        const c = m.centroid;
        const inOld = c && c.x >= oldBbox[0] && c.x <= oldXMax
            && c.y >= oldBbox[1] && c.y <= oldYMax;
        if (inOld)
            continue;
        // Skip MCIDs already owned by the resizing struct (idempotent
        // — drag-bigger that re-covers your own already-claimed MCIDs).
        let owner = null;
        try {
            owner = ownerLookup(m.page, m.mcid);
        }
        catch {
            owner = null;
        }
        if (owner === resizingStructId)
            continue;
        absorbed.push(m);
    }
    return { absorbed, byOwner: mcidsByOwner(absorbed, ownerLookup) };
}
export function detectKickedOutMcids({ mcidsOnPage, resizingStructId, ownerLookup, oldBbox: _oldBbox, newBbox, }) {
    if (!Array.isArray(mcidsOnPage))
        return [];
    if (!Array.isArray(newBbox))
        return [];
    const newXMax = newBbox[0] + newBbox[2];
    const newYMax = newBbox[1] + newBbox[3];
    const out = [];
    for (const m of mcidsOnPage) {
        if (!m?.centroid)
            continue;
        let owner = null;
        try {
            owner = ownerLookup(m.page, m.mcid);
        }
        catch {
            owner = null;
        }
        if (owner !== resizingStructId)
            continue;
        const c = m.centroid;
        const stillIn = c.x >= newBbox[0] && c.x <= newXMax
            && c.y >= newBbox[1] && c.y <= newYMax;
        if (!stillIn)
            out.push(m);
    }
    return out;
}
export function bboxArrayToObject(arr) {
    if (!Array.isArray(arr) || arr.length !== 4)
        return null;
    return { x: arr[0], y: arr[1], w: arr[2], h: arr[3] };
}
export function buildReassociateOp({ kind, mcids, targetStructId, bbox }) {
    if (!kind || !Array.isArray(mcids) || mcids.length === 0)
        return null;
    const k = String(kind).toLowerCase();
    const wireMcids = mcids
        .map((m) => (m && Number.isFinite(m.page) && Number.isFinite(m.mcid))
        ? { page: m.page, mcid: m.mcid } : null)
        .filter((x) => x !== null);
    if (wireMcids.length === 0)
        return null;
    const op = { kind: "reassociate-mcids", reassocKind: k, mcids: wireMcids };
    if (k === "absorb" || k === "kick-out") {
        if (!targetStructId)
            return null;
        op.targetStructId = String(targetStructId);
    }
    if (k === "kick-out" || k === "create") {
        const b = bboxArrayToObject(bbox);
        if (!b)
            return null;
        op.bbox = b;
    }
    return op;
}
// ─── DrawCreateController — browser-only ─────────────────────────────────────
const MIN_DRAW_PX = 6;
export function DrawCreateController(hooks) {
    if (!hooks?.overlayContainer || typeof hooks.onCommit !== "function") {
        throw new Error("DrawCreateController: overlayContainer + onCommit required");
    }
    const root = hooks.overlayContainer;
    const doc = root.ownerDocument || (typeof document !== "undefined" ? document : null);
    if (!doc)
        throw new Error("DrawCreateController: no ownerDocument");
    let drag = null;
    let marqueeEl = null;
    function defaultPixelRectToPdf(pxRect) {
        const scale = (typeof hooks.getScale === "function") ? hooks.getScale() : 1;
        const pageSize = (typeof hooks.getPageSize === "function") ? hooks.getPageSize() : null;
        const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
        const pageH = pageSize && Number.isFinite(pageSize.height) ? pageSize.height : 0;
        const [px, py, pw, ph] = pxRect;
        const xPdf = px / s;
        const wPdf = pw / s;
        const hPdf = ph / s;
        // Canvas y-axis points down; PDF user-space y-axis points up.
        // The PDF lower-left of the rect is at (canvas-y + h) measured
        // from page bottom: yPdf = pageH - (canvasY + canvasH) / s.
        const yPdf = pageH - (py + ph) / s;
        return [xPdf, yPdf, wPdf, hPdf];
    }
    function ensureMarquee() {
        if (marqueeEl)
            return;
        marqueeEl = doc.createElement("div");
        marqueeEl.className = "editor-draw-create-marquee";
        marqueeEl.setAttribute("aria-hidden", "true");
        root.appendChild(marqueeEl);
    }
    function cleanupMarquee() {
        if (marqueeEl?.parentNode) {
            try {
                marqueeEl.parentNode.removeChild(marqueeEl);
            }
            catch { /* best-effort: marquee was already detached */ }
        }
        marqueeEl = null;
    }
    function onPointerDown(ev) {
        if (ev.button !== 0)
            return;
        if (ev.shiftKey || ev.metaKey || ev.ctrlKey)
            return;
        // Yield to existing controllers on bbox / handle pointerdowns.
        if (ev.target && ev.target.closest) {
            if (ev.target.closest(".editor-overlay-bbox"))
                return;
            if (ev.target.closest(".editor-resize-handle"))
                return;
        }
        const rect = root.getBoundingClientRect();
        drag = {
            originPx: { x: ev.clientX - rect.left, y: ev.clientY - rect.top },
        };
        ensureMarquee();
        try {
            root.setPointerCapture?.(ev.pointerId);
        }
        catch { /* best-effort: setPointerCapture unsupported on this element */ }
        if (typeof ev.preventDefault === "function")
            ev.preventDefault();
    }
    function onPointerMove(ev) {
        if (!drag)
            return;
        const rect = root.getBoundingClientRect();
        const cur = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
        const x = Math.min(drag.originPx.x, cur.x);
        const y = Math.min(drag.originPx.y, cur.y);
        const w = Math.abs(cur.x - drag.originPx.x);
        const h = Math.abs(cur.y - drag.originPx.y);
        if (marqueeEl) {
            marqueeEl.style.left = `${x}px`;
            marqueeEl.style.top = `${y}px`;
            marqueeEl.style.width = `${w}px`;
            marqueeEl.style.height = `${h}px`;
        }
    }
    function onPointerUp(ev) {
        if (!drag)
            return;
        const rect = root.getBoundingClientRect();
        const cur = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
        const x = Math.min(drag.originPx.x, cur.x);
        const y = Math.min(drag.originPx.y, cur.y);
        const w = Math.abs(cur.x - drag.originPx.x);
        const h = Math.abs(cur.y - drag.originPx.y);
        try {
            root.releasePointerCapture?.(ev.pointerId);
        }
        catch { /* best-effort: releasePointerCapture unsupported on this element */ }
        cleanupMarquee();
        drag = null;
        if (w < MIN_DRAW_PX || h < MIN_DRAW_PX)
            return;
        const toPdf = (typeof hooks.pixelRectToPdf === "function")
            ? hooks.pixelRectToPdf : defaultPixelRectToPdf;
        const drawnPdf = toPdf([x, y, w, h]);
        const allMcids = (typeof hooks.getMcidsForPage === "function")
            ? hooks.getMcidsForPage() : [];
        const matched = findMcidsInBbox(allMcids, hooks.pageNum, drawnPdf);
        // Tighten if we matched glyphs; otherwise use the loose drawn
        // rect (no MCID centroids = un-tagged region; the user's intent
        // is the rect they drew).
        const tightenedBbox = matched.length > 0
            ? tightenBboxToMcids(matched, drawnPdf)
            : drawnPdf;
        try {
            hooks.onCommit({ tightenedBbox, mcids: matched });
        }
        catch {
            // Host-side commit failure is logged via host's toast; the
            // controller has no recovery path beyond clearing drag state.
        }
    }
    root.addEventListener("pointerdown", onPointerDown);
    root.addEventListener("pointermove", onPointerMove);
    root.addEventListener("pointerup", onPointerUp);
    return {
        destroy() {
            root.removeEventListener("pointerdown", onPointerDown);
            root.removeEventListener("pointermove", onPointerMove);
            root.removeEventListener("pointerup", onPointerUp);
            cleanupMarquee();
            drag = null;
        },
        _isDrawingForTest() { return drag !== null; },
    };
}
